import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  CircularProgress,
} from '@mui/material'
import {
  TrendingUp,
  Users,
  DollarSign,
  Calendar,
  Plus,
  Eye,
  Edit,
} from 'lucide-react'
import API from '../../utils/API'
import { useAuth } from '../../utils/authContext'

const StatCard = ({ label, value, change, icon: Icon, colorScheme = 'primary' }) => {
      const getColorScheme = () => {
        switch (colorScheme) {
          case 'success': return { bg: 'linear-gradient(135deg, #00C851 0%, #4CAF50 100%)', icon: '#00C851' }
          case 'warning': return { bg: 'linear-gradient(135deg, #FFBB33 0%, #FFC107 100%)', icon: '#FFBB33' }
          case 'error': return { bg: 'linear-gradient(135deg, #FF4444 0%, #F44336 100%)', icon: '#FF4444' }
          default: return { bg: 'linear-gradient(135deg, #0029FF 0%, #4A5BFF 100%)', icon: '#0029FF' }
        }
      }
      
      const colors = getColorScheme()
      
      return (
        <Card sx={{ 
          background: 'linear-gradient(135deg, #ffffff 0%, #F8FAFC 100%)',
          border: '1px solid #E2E8F0',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
          },
          transition: 'all 0.3s ease-in-out',
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography color="grey.600" variant="body2" fontWeight="500">
                  {label}
                </Typography>
                <Typography variant="h4" fontWeight="700" color="grey.800" sx={{ mt: 0.5 }}>
                  {value}
                </Typography>
                {change && (
                  <Typography 
                    variant="body2" 
                    color={change > 0 ? 'success.main' : 'error.main'}
                    fontWeight="500"
                    sx={{ mt: 0.5 }}
                  >
                    {change > 0 ? '↗' : '↘'} {Math.abs(change)}%
                  </Typography>
                )}
              </Box>
              <Avatar
                sx={{
                  background: colors.bg,
                  color: 'white',
                  width: 64,
                  height: 64,
                  boxShadow: `0 4px 12px ${colors.icon}20`,
                }}
              >
                <Icon size={28} />
              </Avatar>
            </Box>
          </CardContent>
        </Card>
      )
    }

const QuickActionCard = ({ title, description, icon: Icon, action, variant = 'outlined' }) => (
      <Card
        sx={{
          cursor: 'pointer',
          transition: 'all 0.3s ease-in-out',
          background: 'linear-gradient(135deg, #ffffff 0%, #F8FAFC 100%)',
          border: '1px solid #E2E8F0',
          '&:hover': {
            transform: 'translateY(-6px)',
            boxShadow: '0 12px 40px rgba(0, 41, 255, 0.15)',
            border: '1px solid #4A5BFF',
          },
        }}
        onClick={action}
      >
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 2 }}>
            <Avatar
              sx={{
                background: 'linear-gradient(135deg, #0029FF 0%, #4A5BFF 100%)',
                color: 'white',
                width: 72,
                height: 72,
                boxShadow: '0 6px 20px rgba(0, 41, 255, 0.3)',
              }}
            >
              <Icon size={28} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="600" color="grey.800">
                {title}
              </Typography>
              <Typography variant="body2" color="grey.600" sx={{ mt: 0.5 }}>
                {description}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              onClick={action}
              fullWidth
              sx={{
                background: 'linear-gradient(135deg, #0029FF 0%, #4A5BFF 100%)',
                fontWeight: 600,
                '&:hover': {
                  background: 'linear-gradient(135deg, #001ECC 0%, #0029FF 100%)',
                },
              }}
            >
              {title}
            </Button>
          </Box>
        </CardContent>
      </Card>
    )

const RecentActivityItem = ({ activity, time, type }) => {
      const getTypeColor = (type) => {
        switch (type) {
          case 'event': return { bg: '#E8F5E8', color: '#00C851', border: '#00C851' }
          case 'order': return { bg: '#E8F0FF', color: '#0029FF', border: '#0029FF' }
          case 'financial': return { bg: '#FFF3E0', color: '#FF6B35', border: '#FF6B35' }
          case 'system': return { bg: '#F5F5F5', color: '#718096', border: '#CBD5E0' }
          default: return { bg: '#F5F5F5', color: '#718096', border: '#CBD5E0' }
        }
      }
      
      const colors = getTypeColor(type)
      
      return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={type.charAt(0).toUpperCase() + type.slice(1)}
              size="small"
              sx={{
                backgroundColor: colors.bg,
                color: colors.color,
                border: `1px solid ${colors.border}`,
                fontWeight: 500,
                fontSize: '0.75rem',
              }}
            />
            <Typography variant="body2" color="grey.700" fontWeight="500">{activity}</Typography>
          </Box>
          <Typography variant="caption" color="grey.500" fontWeight="500">
            {time}
          </Typography>
        </Box>
      )
    }

export default function Home() {
  const { showrunner, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [showrunnerData, setShowrunnerData] = useState(null)
  const [financialData, setFinancialData] = useState(null)
  const [recentOrders, setRecentOrders] = useState([])
  const [shows, setShows] = useState([])
  
  useEffect(() => {
    if (authLoading) return
    if (!showrunner?.id) {
      setLoading(false)
      return
    }
    fetchDashboardData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showrunner, authLoading])
  
  const fetchDashboardData = async () => {
        if (!showrunner?.id) {
          setLoading(false)
          return
        }
        
        try {
          setLoading(true)
          
          const showrunnerId = showrunner.id
          
          // Fetch financial data
          const financialResponse = await API.getFinancialData(showrunnerId, {
            time_range: '30d'
          })
          setFinancialData(financialResponse.data)
          
          // Fetch recent orders
          const ordersResponse = await API.getRecentOrders(showrunnerId, 10, 7)
          setRecentOrders(ordersResponse.data)
          
          // Fetch shows
          const showsResponse = await API.getShows(`?showrunner_id=${showrunnerId}`)
          setShows(showsResponse.data)
          
          setShowrunnerData(showrunner)
        } catch (error) {
          console.error('Error fetching dashboard data:', error)
        } finally {
          setLoading(false)
        }
      }
  
  const calculateStats = () => {
    if (!financialData || !recentOrders || !shows) return []
    
    const totalRevenue = financialData.total_revenue || 0
    const totalTicketsSold = financialData.total_tickets_sold || 0
    const activeEvents = shows.filter(show => show.status === 'active').length
    const pendingOrders = recentOrders.filter(order => order.status === 'pending').length
    
    return [
      {
        label: 'Total Revenue',
        value: `$${totalRevenue.toLocaleString()}`,
        change: financialData.revenue_change || 0,
        icon: DollarSign,
        colorScheme: 'success',
      },
      {
        label: 'Tickets Sold',
        value: totalTicketsSold.toLocaleString(),
        change: financialData.tickets_change || 0,
        icon: Users,
        colorScheme: 'primary',
      },
      {
        label: 'Active Events',
        value: activeEvents.toString(),
        change: 0,
        icon: Calendar,
        colorScheme: 'secondary',
      },
      {
        label: 'Pending Orders',
        value: pendingOrders.toString(),
        change: 0,
        icon: TrendingUp,
        colorScheme: 'warning',
      },
    ]
  }
  
      const getRecentActivity = () => {
        if (!recentOrders) return []
        
        return recentOrders.slice(0, 4).map(order => ({
          activity: `New order received for "${order.show_name || order.show_title || 'Event'}"`,
          time: formatTimeAgo(order.created_at),
          type: 'order'
        }))
      }
  
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMinutes = Math.floor((now - date) / (1000 * 60))
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes} min ago`
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)} hour${Math.floor(diffInMinutes / 60) > 1 ? 's' : ''} ago`
    } else {
      return `${Math.floor(diffInMinutes / 1440)} day${Math.floor(diffInMinutes / 1440) > 1 ? 's' : ''} ago`
    }
  }
  
  const quickActions = [
    {
      title: 'Create New Event',
      description: 'Start planning your next big show',
      icon: Plus,
      action: () => navigate('/showrunner/events/create'),
    },
    {
      title: 'View Reports',
      description: 'Analyze your sales and audience data',
      icon: Eye,
      action: () => navigate('/showrunner/financial'),
    },
    {
      title: 'Manage Events',
      description: 'Edit existing events and settings',
      icon: Edit,
      action: () => navigate('/showrunner/events'),
    },
  ]
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    )
  }
  
  const stats = calculateStats()
  const recentActivity = getRecentActivity()
  
  return (
    <Box>
      {/* Welcome Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
          Welcome back, {showrunnerData?.first_name || 'Showrunner'}! 👋
        </Typography>
        <Typography color="grey.600">
          Here's what's happening with your events today.
        </Typography>
      </Box>
      
      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>
      
      {/* Quick Actions & Recent Activity */}
      <Grid container spacing={4}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Quick Actions
              </Typography>
              <Grid container spacing={2}>
                {quickActions.map((action, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <QuickActionCard {...action} />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Recent Activity
              </Typography>
              <Box>
                {recentActivity.map((item, index) => (
                  <Box key={index}>
                    <RecentActivityItem {...item} />
                    {index < recentActivity.length - 1 && <hr style={{ margin: '8px 0' }} />}
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
